import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Badge, Box, CompactPagination, Typography } from '@kc-console/common-ui';
import { getCustomFormatDate } from '@kc-console/core';

import useAnnouncementPagination from './useAnnouncementPagination';
import { DATE_FORMAT, isNewAnnouncement, PAGE_SIZE } from './utils';

import type { AnnouncementListEntry } from './types';

/** 목록 한 행의 고정 높이(px). 한 페이지 항목이 1~2개여도 List 높이가 PAGE_SIZE개 기준으로 유지되어 모달 높이가 흔들리지 않습니다. */
const LIST_ITEM_HEIGHT = 40;

interface AnnouncementArticleListProps {
  /** 목록 API 전체 조회로 받아온 새소식 목록 */
  announcements: AnnouncementListEntry[];
  /** 위젯에서 선택된 새소식 ID (초기 페이지 계산 기준) */
  initialAnnouncementId: string;
  /** 목록에서 새소식을 선택할 때 호출 */
  onSelect: (id: string) => void;
  /** 현재 선택된 새소식 ID (bold 표시 기준) */
  selectedId: string;
}

/**
 * 새소식 카테고리의 다른 글 목록을 페이지 단위로 보여줍니다.
 * @description
 * - 한 페이지에 PAGE_SIZE 개씩 표시하고, 첫/마지막 페이지에서 이전·다음 버튼을 비활성화합니다.
 * - 게시일 기준 7일 이내 글에는 New 뱃지를, 선택된 글에는 굵은 제목을 표시합니다.
 */
const AnnouncementArticleList = ({
  announcements,
  initialAnnouncementId,
  onSelect,
  selectedId,
}: AnnouncementArticleListProps) => {
  const { page, pageAnnouncements, goToPage } = useAnnouncementPagination(
    announcements,
    initialAnnouncementId,
  );
  const now = new Date();

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Box display="flex" gap="4px" alignItems="center" paddingX="8px">
        <Typography variant="label/medium/md" color="text/weak/default">
          새소식 카테고리의 다른 글
        </Typography>
        <Typography variant="label/medium/md" color="text/weakest/default">
          {announcements.length}
        </Typography>
      </Box>

      <List>
        {pageAnnouncements.map(announcement => {
          const isSelected = announcement.id === selectedId;

          return (
            <ListItem key={announcement.id}>
              <Box display="flex" flex="1" gap="8px" alignItems="center" minWidth={0}>
                {isNewAnnouncement(announcement.publishedAt, now) && (
                  <Badge variant="outline" color="purple" label="New" />
                )}
                <TitleButton
                  type="button"
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => onSelect(announcement.id)}
                >
                  <TitleText
                    variant={isSelected ? 'label/semibold/md' : 'body/normal/md'}
                    color="text/default"
                    component="span"
                  >
                    {announcement.title}
                  </TitleText>
                </TitleButton>
              </Box>
              <Typography variant="body/normal/xs" color="text/weaker/default" component="span">
                {announcement.publishedAt &&
                  getCustomFormatDate(announcement.publishedAt, DATE_FORMAT)}
              </Typography>
            </ListItem>
          );
        })}
      </List>

      <Box display="flex" gap="12px" alignItems="center" justifyContent="center" paddingTop="12px">
        <CompactPagination
          page={page}
          count={announcements.length}
          rowsPerPage={PAGE_SIZE}
          hideLabelDisplayRows
          onPageChange={(_, nextPage) => goToPage(nextPage)}
        />
      </Box>
    </Box>
  );
};

export default AnnouncementArticleList;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  min-height: ${LIST_ITEM_HEIGHT * PAGE_SIZE}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListItem = styled.li(
  ({ theme }) => css`
    display: flex;
    min-height: ${LIST_ITEM_HEIGHT}px;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    border-bottom: 1px solid ${theme.color['border/weaker/default']};
  `,
);

const TitleButton = styled.button`
  display: flex;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`;

const TitleText = styled(Typography)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
